import { useState, useCallback, useMemo } from 'react';
import useOrganisation from 'state/organisation/hooks/useOrganisation';
import usePaymentConfig from 'state/paymentConfig/hooks/usePaymentConfig';
import useOrgLevel from 'utils/hooks/useOrgLevel';
import { getSectionsForUserType } from './config';
import { getFilteredSections } from './utils';
import type { SidebarState, UserType } from './types';
import type { PAYMENT_CONFIG } from 'state/paymentConfig/type';

export function useEntityPermissions():
  | PAYMENT_CONFIG.IEntityPermissionObject
  | undefined {
  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const { paymentConfig } = usePaymentConfig();

  return useMemo(() => {
    return paymentConfig?.msoPermissionsList?.entityContexts?.find(
      (entity) => entity.id === activeOrg?.treez_entity_id
    );
  }, [
    paymentConfig?.msoPermissionsList?.entityContexts,
    activeOrg?.treez_entity_id,
  ]);
}

export function useSidebar(): SidebarState {
  const [isHovered, setIsHovered] = useState(false);

  const { organisation } = useOrganisation();
  const { activeOrg } = organisation;
  const isOrgLevel = useOrgLevel();
  const entityPermissions = useEntityPermissions();

  const userType: UserType = useMemo(() => {
    if (activeOrg?.is_partner === true) {
      return 'partner';
    }
    return 'dispensary';
  }, [activeOrg?.is_partner]);

  const sections = useMemo(() => {
    const baseSections = getSectionsForUserType(userType);
    return getFilteredSections(baseSections, entityPermissions, isOrgLevel);
  }, [userType, entityPermissions, isOrgLevel]);

  const isCollapsed = !isHovered;

  const handleSetHovered = useCallback((hovered: boolean) => {
    setIsHovered(hovered);
  }, []);

  return {
    isHovered,
    isCollapsed,
    userType,
    isOrgLevel,
    sections,
    setHovered: handleSetHovered,
  };
}

export default useSidebar;
